/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService, I18nService } from '@dm/core';
import { FileSystemModeE } from 'app/common/constants';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { MO } from 'app/common/constants/mo';
import { type TaskOptionsArgs, TaskService, ToolService } from 'app/common/services';
import { type HyperCdpSchedule, type ProtectGroup } from 'app/common/types/mo';
import { type Observable, forkJoin } from 'rxjs';

import { ConfigPolicyStrService } from './create/config-policy/config-policy-str.service';
import { objectTypeE } from './create/create-str.service';
import { ScheduleStrService } from './schedule-str.service';

export interface HyperCdpScheduleExtends extends HyperCdpSchedule {
    strategy?: Array<string>;
}

const SCHEDULE_ALL_VSTORE_ID = '-1';

@Injectable({ providedIn: 'root' })
export class ScheduleService {
    constructor(
        private http: HttpService,
        private configStr: ConfigPolicyStrService,
        private tool: ToolService,
        private str: ScheduleStrService,
        private i18nService: I18nService,
        private taskService: TaskService,
    ) { }

    // 批量查询LUN
    getBatchLunRest(): Rest {
        // 排除容器LUN
        return this.http.makeUrl('lun.getBatch').setFilters([
            // 排除容器LUN 和 带内LUN
            { field: 'USAGETYPE', value: '5_6_7_8', matchMode: '!!' },
        ]);
    }
    // 批量查询文件系统
    getBatchFsRest(vstoreId?: string): Rest {
        return this.http.makeUrl('fileSystem.getAssociate')
            .setParams({
                TYPE: MO.FILE_SYSTEM,
                ASSOCIATEOBJTYPE: MO.SNAPSHOT_SCHEDULE,
                ASSOCIATEOBJID: 0,
                vstoreId: vstoreId || SCHEDULE_ALL_VSTORE_ID,
            });
    }

    getProtectGroup(vstoreId = SCHEDULE_ALL_VSTORE_ID): Rest {
        const ProtecgGroupRest = this.http.makeUrl('protectGroup.getBatch')
            .setParams({ RSSTYPE: 22, vstoreId })
            .setFilters([
                { field: 'usageType', value: 0, matchMode: '::' },
            ]);
        return ProtecgGroupRest;
    }

    getFs(vstoreId = SCHEDULE_ALL_VSTORE_ID): Rest {
        return this.http.makeUrl('fileSystem.getBatch').setParams({ vstoreId });
    }

    getLun(vstoreId = SCHEDULE_ALL_VSTORE_ID): Rest {
        const LunRest = this.http.makeUrl('lun.getBatch')
            .setFilters([
                { field: 'USAGETYPE', matchMode: '::', value: 0 },
                // 0: 普通LUN； 6: 计算节点un
                { field: 'SUBTYPE', matchMode: '::', value: '0_6' },
                // 排除容器LUN
                { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
            ])
            .setParams({ RSSTYPE: 36, vstoreId });
        return LunRest;
    }

    // 查询下一个可用ID
    getNextId(type: number): Observable<HttpResponse> {
        const rest = this.http.makeUrl('storagePool.nextavailableid');
        rest.setParams({ TYPE: type });
        return this.http.get(rest, { doException: false });
    }

    // 关联查询LUN
    getAssociateLUNRest(scheduleId: string, vstoreId: string): Rest {
        return this.http.makeUrl('hyperCdpSchedule.getAsscociatelun')
            .setParams({
                ASSOCIATEOBJTYPE: 57527,
                ASSOCIATEOBJID: scheduleId,
                vstoreId,
            });
    }

    // 关联查询LUN数量 ???
    getAssociateLUNCountRest(scheduleId: string, vstoreId: string): Rest {
        return this.http.makeUrl('lun.count')
            .setParams({
                ASSOCIATEOBJTYPE: 57527,
                ASSOCIATEOBJID: scheduleId,
                vstoreId,
            });
    }

    // 关联查询文件系统
    getAssociateFSRest(scheduleId: string, vstoreId?: string): Rest {
        return this.http.makeUrl('fileSystem.getAssociate')
            .setParams({
                TYPE: MO.FILE_SYSTEM,
                ASSOCIATEOBJTYPE: MO.SNAPSHOT_SCHEDULE,
                ASSOCIATEOBJID: scheduleId,
                vstoreId: vstoreId || -1,
            });
    }

    // 关联查询文件系统数量
    getAssociateFSCountRest(scheduleId: string, vstoreId?: string): Rest {
        return this.http.makeUrl('fileSystem.getAssociateCount')
            .setParams({
                TYPE: MO.FILE_SYSTEM,
                ASSOCIATEOBJTYPE: MO.SNAPSHOT_SCHEDULE,
                ASSOCIATEOBJID: scheduleId,
                vstoreId: vstoreId || -1,
            });
    }

    // 关联查询保护组
    getAssociatePGRest(scheduleId: string, vstoreId: string): Rest {
        return this.http.makeUrl('hyperCdpSchedule.getAsscociatePg')
            .setParams({
                ASSOCIATEOBJTYPE: MO.SNAPSHOT_SCHEDULE,
                ASSOCIATEOBJID: scheduleId,
                vstoreId,
            });
    }

    // 关联查询保护组数量
    getAssociatePGCountRest(scheduleId: string, vstoreId: string): Rest {
        return this.http.makeUrl('hyperCdpSchedule.getPGCount')
            .setParams({
                ASSOCIATEOBJTYPE: MO.SNAPSHOT_SCHEDULE,
                ASSOCIATEOBJID: scheduleId,
                vstoreId,
            });
    }

    getTargetSource(id: number): Observable<HttpResponse[]> {
        const ProtectGroupRest = this.http.makeUrl('hyperCdpSchedule.getAsscociatePg')
            .setParams({
                ASSOCIATEOBJTYPE: MO.SNAPSHOT_SCHEDULE,
                ASSOCIATEOBJID: id,
            });
        const LunRest = this.http.makeUrl('hyperCdpSchedule.getAsscociatelun')
            .setParams({
                ASSOCIATEOBJTYPE: MO.SNAPSHOT_SCHEDULE,
                ASSOCIATEOBJID: id,
            });
        // 打开PG开关
        const protecgGroup$ = this.http.get(ProtectGroupRest);
        const lun$ = this.http.get(LunRest);
        return forkJoin([lun$, protecgGroup$]);
    }

    getBatchSource(): Observable<HttpResponse[]> {
        const ProtecgGroupRest = this.http.makeUrl('protectGroup.getBatch')
            .setParams({ RSSTYPE: 22 })
            .setFilters([
                { field: 'usageType', value: 0, matchMode: '::' },
            ]);
        const LunRest = this.http.makeUrl('lun.getBatch')
            .setFilters([
                { field: 'USAGETYPE', matchMode: '::', value: 0 },
                // 0: 普通LUN； 6: 计算节点un
                { field: 'SUBTYPE', matchMode: '::', value: '0_6' },
                // 排除容器LUN
                { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
            ])
            .setParams({ RSSTYPE: 36 });
        // 打开PG开关
        const protecgGroup$ = this.http.get(ProtecgGroupRest);
        const lun$ = this.http.get(LunRest);
        return forkJoin([protecgGroup$, lun$]);
    }

    /**
     * 批量查询HyperCDPSchedule
     * CDP计划根据选择租户查询
     * 传入vstoreId说明从服务-文件系统入口进入，需要根据文件系统租户查询
     */
    getBatchRest(type?: ScheduleTypeE, vstoreId?: string): Rest {
        const rest = this.http.makeUrl('hyperCdpSchedule.getBatch', undefined, !vstoreId);

        if (vstoreId) {
            rest.setParams({
                vstoreId,
            });
        }
        rest.setParams({
            SCHEDULETYPE: 1,
        });
        rest.setFilters([{
            field: 'OBJECTTYPE', matchMode: '::', value:
                type && type === ScheduleTypeE.File ? objectTypeE.file : objectTypeE.block,
        }]);
        return rest;
    }

    // 创建HyperCDPSchedule
    create(param: HyperCdpSchedule, vstoreId?: string, option?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.post');
        param.SCHEDULETYPE = 1;
        param.vstoreId = vstoreId;
        return this.http.post(rest, param, option);
    }

    // 修改hyperCdpSchedule
    modify(id: string, param: HyperCdpSchedule, vstoreId?: string, option?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.put').format(id);
        param.SCHEDULETYPE = 1;
        param.vstoreId = vstoreId;
        return this.http.put(rest, param, option);
    }
    // 启动hyperCdpSchedule
    activate(id: string, vstoreId?: string, option?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.activate');
        rest.setParams({
            SCHEDULETYPE: 1,
        });
        const body = {
            ID: id,
            ENABLESCHEDULE: true,
            vstoreId,
        };
        return this.http.put(rest, body, option);
    }

    // 停止hyperCdpSchedule
    deactivate(id: string, vstoreId?: string, option?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.activate');
        rest.setParams({
            SCHEDULETYPE: 1,
        });
        const body = {
            ID: id,
            ENABLESCHEDULE: false,
            vstoreId,
        };
        return this.http.put(rest, body, option);
    }

    // 查询单个hyperCdpSchedule
    getSingle(id: number, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.get').format(id);
        rest.setParams({
            SCHEDULETYPE: 1,
            vstoreId,
        });
        return this.http.get(rest, options);
    }

    getCount(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.getCount');
        rest.setParams({
            SCHEDULETYPE: 1,
        });
        return this.http.get(rest, options);
    }

    getPgCount(): Rest {
        const rest = this.http.makeUrl('protectgroup.getCount')
            .setParams({ RSSTYPE: 22 });
        return rest;
    }

    getLunCount(): Rest {
        const rest = this.http.makeUrl('lun.count')
            .setFilters([
                { field: 'USAGETYPE', matchMode: '::', value: 0 },
                // 0: 普通LUN； 6: 计算节点un
                { field: 'SUBTYPE', matchMode: '::', value: '0_6' },
            ])
            .setParams({ RSSTYPE: 36 });
        return rest;
    }

    // 删除hyperCdpSchedule
    delete(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.get').format(id);
        rest.setParams({ vstoreId });
        return this.http.delete(rest, options);
    }
    addAssociateMember(id: string, memberId: string, memberType: number,
        options?: BaseRequestOptionsArgs, vstoreId?: string): Observable<HttpResponse> {
        if (Number(memberType) === MO.PROTECT_GROUP) {
            return this.addAssociatePG(id, memberId, vstoreId, options);
        } else if (Number(memberType) === MO.LUN) {
            return this.addAssociateLun(id, memberId, vstoreId, options);
        } else {
            return this.addAssociateFs(id, memberId, vstoreId, options);
        }
    }

    // 关联hyperCdpSchedulePG
    addAssociatePG(id: string, PGid: string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.addAssociate');
        const body = {
            ID: id,
            ASSOCIATEOBJTYPE: MO.PROTECT_GROUP,
            ASSOCIATEOBJID: PGid,
            SCHEDULETYPE: 1,
            vstoreId,
        };
        return this.http.post(rest, body, options);
    }

    // 通过omtask 添加PG到CDP Schedule
    addPG2Schedule(objName: string, params: object, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('schedule.add', 'V2');
        return this.taskService.post('addPG2Schedule', objName, rest, params, options);
    }

    // 关联hyperCdpScheduleLUN
    addAssociateLun(id: string, lunId: string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.addAssociate');
        const body = {
            ID: id,
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: lunId,
            SCHEDULETYPE: 1,
            vstoreId,
        };
        return this.http.post(rest, body, options);
    }

    // 关联hyperCdpSchedule文件系统
    addAssociateFs(id: string, fsId: string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.addAssociateFs');
        const body = {
            ID: fsId,
            TIMINGSNAPSHOTSCHEDULEID: id,
            vstoreId,
        };
        return this.http.post(rest, body, options);
    }

    // 通过CDP NAME关联hyperCdpSchedule文件系统
    addAssociateFsByName(name: string, fsId: string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.addAssociateFs');
        const body = {
            ID: fsId,
            scheduleName: name,
            vstoreId,
        };
        return this.http.post(rest, body, options);
    }

    removeAssociate(id: string, memberId: string, memberType: number,
        options?: BaseRequestOptionsArgs, vstoreId?: string): Observable<HttpResponse> {
        if (Number(memberType) === MO.PROTECT_GROUP) {
            return this.removeAssociatePG(id, memberId, options);
        } else if (Number(memberType) === MO.LUN) {
            return this.removeAssociateLun(id, memberId, options);
        } else {
            return this.removeAssociateFs(id, memberId, options, vstoreId);
        }
    }

    // 删除hyperCdpSchedule PG关联
    removeAssociatePG(id: string, pgId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.removeAssociatePg');
        rest.setParams({
            ID: id,
            ASSOCIATEOBJTYPE: MO.PROTECT_GROUP,
            ASSOCIATEOBJID: pgId,
            SCHEDULETYPE: 1,
        });
        return this.http.delete(rest, options);
    }

    // 删除hyperCdpSchedule lun关联
    removeAssociateLun(id: string, lunId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.removeAssociate');
        rest.setParams({
            ID: id,
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: lunId,
            SCHEDULETYPE: 1,
        });
        return this.http.delete(rest, options);
    }

    // 删除hyperCdpSchedule 文件系统关联
    removeAssociateFs(id: string, fsId: string, options?: BaseRequestOptionsArgs, vstoreId?: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.removeAssociateFs');
        rest.setParams({
            ID: fsId,
            TIMINGSNAPSHOTSCHEDULEID: id,
            vstoreId,
        });
        return this.http.delete(rest, options);
    }

    // 获取所有多租户的Rest
    getVstoreRest(): Rest {
        return this.http.makeUrl('vstore.getBatch');
    }

    /**
     * Renders strategy
     * @param schedule
     * @param [showRetainNum] 是否显示保留个数
     * @returns
     */
    renderStrategy(schedule: HyperCdpSchedule, showRetainNum = false): string[] {
        const label: string[] = [];
        if (!this.tool.isInvalidNum(schedule.FREQUENCYVALUE)) {
            let everyNum = Number(schedule.FREQUENCYVALUE);
            const space = this.i18nService.language === 'zh' ? '' : ' ';
            const executeLab = [];

            if (everyNum >= 3600) {
                const hours = Math.floor(everyNum / 3600);
                executeLab.push(hours + space + this.str.table.hoursLab);
                everyNum -= hours * 3600;
            }

            if (everyNum >= 60) {
                const minutes = Math.floor(everyNum / 60);
                executeLab.push(minutes + space + this.str.table.minutesLab);
                everyNum -= minutes * 60;
            }

            if (everyNum > 0) {
                executeLab.push(everyNum + space + this.str.table.secondsLab);
            }

            let fixedLabel = this.configStr.title.executePolicyTime
                .replace('{0}', executeLab.join(space))
                .replace('{1}', '').trim();

            if (showRetainNum) {
                // 转换成 xx天xx小时xx分xx秒。逻辑与详情页中一致。
                const totalSec: number = Number(schedule.FREQUENCYVALUE) * Number(schedule.FREQUENCYNUM);
                // 1day = 86400s; 1hour = 3600s;
                const day = Math.floor(totalSec / 86400);
                const hour = Math.floor((totalSec - (day * 86400)) / 3600);
                const min = Math.floor((totalSec - (day * 86400) - (hour * 3600)) / 60);
                const sec = Math.floor(totalSec - (day * 86400) - (hour * 3600) - (min * 60));
                const fixedRetainLabel = this.configStr.title.etentionPeriod
                    .replace('{0}', schedule.FREQUENCYNUM)
                    .replace('{1}', String(day))
                    .replace('{2}', String(hour))
                    .replace('{3}', String(min))
                    .replace('{4}', String(sec));

                fixedLabel += ', ' + fixedRetainLabel;
            }

            label.push(fixedLabel);
        }
        if (!this.tool.isInvalidNum(schedule.DAYMINUTE)) {
            const minLabel = Number(schedule.DAYMINUTE) < 10 ? '0' + schedule.DAYMINUTE : schedule.DAYMINUTE;
            const dayL = String(schedule.DAYHOURS.slice(1, -1)).split(',')
                .map((item) => (Number(item) < 10 ? '0' + item : item) + ':' + minLabel).join(', ');
            let dayLabel = this.configStr.title.executePolicyDay
                .replace('{0}', dayL);

            if (showRetainNum) {
                const dayRetainLabel = this.configStr.title.retainNum + schedule.DAILYSNAPSHOTNUM;

                dayLabel += ', ' + dayRetainLabel;
            }

            label.push(dayLabel);
        }

        if (schedule.STARTTIMEOFWEEK) {
            const weekDay = String(schedule.WEEKLYDAYS.slice(1, -1)).split(',')
                .map((item) => this.configStr.dateOpt.find((a) => a.value === Number(item)).label).join(', ');
            const timeL = schedule.STARTTIMEOFWEEK;
            let weekLabel = this.configStr.title.executePolicyWeek
                .replace('{0}', weekDay)
                .replace('{1}', timeL);

            if (showRetainNum) {
                const weekRetainLabel = this.configStr.title.retainNum + schedule.WEEKLYSNAPSHOTNUM;

                weekLabel += ', ' + weekRetainLabel;
            }

            label.push(weekLabel);
        }

        if (schedule.STARTTIMEOFMONTH) {
            const dates = String(schedule.MONTHDAYS.slice(1, -1)).split(',')
                .map((item) => Number(item) === 99 ? this.configStr.title.lastday : Number(item)).join(', ');
            const timeL = schedule.STARTTIMEOFMONTH;
            let monthLabel = this.configStr.title.executePolicyMonth
                .replace('{0}', dates)
                .replace('{1}', timeL);

            if (showRetainNum) {
                const monthRetainLabel = this.configStr.title.retainNum + schedule.MONTHSNAPSHOTNUM;

                monthLabel += `, ${monthRetainLabel}`;
            }

            label.push(monthLabel);
        }

        return label;
    }

    /**
     * 根据LUN组ID查询保护组00
     * @param lunGroupId
     * @param options
     */
    getPgByLunGroup(lunGroupId: string, options?: BaseRequestOptionsArgs, vstoreId?: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('protectGroup.getBatch');
        rest.setFilters([
            { field: 'lunGroupId', matchMode: '::', value: lunGroupId },
            { field: 'usageType', value: 0, matchMode: '::' },
        ]);
        rest.setParams({ vstoreId });
        return this.http.get(rest, options);
    }

    /**
     * 创建保护组
     * @param param
     * @param option
     */
    createPg(param: ProtectGroup, option?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('protectGroup.post');
        return this.http.post(rest, param, option);
    }

    /**
     * FS获取内置CDP计划
     */
    getDefaultCdpSchedule(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('hyperCdpSchedule.getBatch', undefined, true);

        rest.setParams({
            SCHEDULETYPE: 1,
        });
        rest.setFilters([
            { field: 'OBJECTTYPE', matchMode: '::', value: objectTypeE.file },
            { field: 'isBuildinSchedule', matchMode: '::', value: 1 },
        ]);

        return this.http.get(rest, options);
    }

    /**
     * 获取模板创建源文件系统CDP
     */
    getOrgCdpSchedule(vstoreId: string, scheduleId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getBatchRest(ScheduleTypeE.File, vstoreId);

        rest.setFilters([
            { field: 'ID', matchMode: '::', value: scheduleId },
        ]);

        return this.http.get(rest, options);
    }

    /**
     * 查询系统定时计划策略的最大保留个数
     */
    getScheduleReservedNum(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('totalScheduleReservedNum.get');
        return this.http.get(rest, options);
    }
}
